import type { AuthProvider } from "@refinedev/core";

interface ClerkAuthGetter {
  (): {
    isLoaded: boolean;
    isSignedIn: boolean | undefined;
    signOut: (options?: { redirectUrl?: string }) => Promise<void>;
  };
}

interface ClerkUserGetter {
  (): {
    user: {
      id: string;
      fullName: string | null;
      primaryEmailAddress: { emailAddress: string } | null;
      imageUrl: string;
    } | null | undefined;
  };
}

/**
 * Creates a Refine AuthProvider that reads Clerk state at call time (not creation time).
 * Accepts getter functions so the auth check always reads the latest Clerk state.
 */
export function createAuthProvider(
  getAuth: ClerkAuthGetter,
  getUser: ClerkUserGetter,
): AuthProvider {
  return {
    login: async () => {
      return { success: true, redirectTo: "/login" };
    },
    logout: async () => {
      const auth = getAuth();
      await auth.signOut({ redirectUrl: "/login" });
      return { success: true, redirectTo: "/login" };
    },
    check: async () => {
      const auth = getAuth();
      // isSignedIn is true/false once loaded, undefined while loading
      if (auth.isSignedIn === true) {
        return { authenticated: true };
      }
      if (auth.isSignedIn === false) {
        return { authenticated: false, redirectTo: "/login" };
      }
      // Still loading (isSignedIn === undefined) — don't redirect yet
      return { authenticated: true };
    },
    getIdentity: async () => {
      const { user } = getUser();
      if (user) {
        return {
          id: user.id,
          name: user.fullName,
          email: user.primaryEmailAddress?.emailAddress,
          avatar: user.imageUrl,
        };
      }
      return null;
    },
    onError: async (error) => {
      if (error.status === 401) {
        return { redirectTo: "/login" };
      }
      return { error };
    },
  };
}
